import { useEffect, useState } from 'react'

/* ==================================================================== */
/*  Google Sheet as a no-backend content source.                         */
/*                                                                        */
/*  The frontend reads a published Google Sheet directly (Google's own    */
/*  gviz endpoint returns JSON, so commas in the copy are safe and no     */
/*  third-party proxy is involved). Each tab in the sheet becomes an      */
/*  array of row objects keyed by the header row, e.g.                     */
/*      [{ sign: 'Aries', date: '2026-08-12', text: '…' }, …]              */
/*                                                                        */
/*  SETUP (one-time, no code changes after this):                         */
/*   1. Create a Google Sheet with the tabs documented in                 */
/*      src/data/cosmosSheet.ts (DailyHoroscope, Yearly2026, Muhurat).    */
/*   2. Share → "Anyone with the link" → Viewer.                          */
/*   3. Paste the sheet ID (the long string in its URL between /d/ and    */
/*      /edit) into SHEET_ID below.                                        */
/*                                                                        */
/*  Until an ID is set, the bundled fallback demo content is shown, so    */
/*  the UI always works. Content edits in the sheet go live with no       */
/*  deploy; the last good fetch is cached so a failed fetch never blanks  */
/*  the page.                                                             */
/* ==================================================================== */

// Default spreadsheet (holds the "Your 2026" content). Public read-only doc id,
// not a secret; hardcoded here so content is live with no env setup.
const SHEET_ID = '1N7U1c3xGVy66EPBW9YzzkZKM-Q6xeIzi'

export type SheetRow = Record<string, string>

// Each logical tab (the `sheet` value in content config) can live in its own
// spreadsheet and physical tab. The client uploads one .xlsx per Cosmos feature,
// so we map them here and keep the app code semantic:
//   • sheetId omitted → the default SHEET_ID above
//   • tab omitted     → the spreadsheet's first tab
const TAB_SOURCE: Record<string, { sheetId?: string; tab?: string }> = {
  Yearly2026: { tab: 'Sheet1' },
  DailyHoroscope: { sheetId: '1N4eiFbyuR78DJChkxH10re_QttfNqVeG' },
  // Live Muhurat sheet (2026 Car / House / Marriage windows). First tab,
  // columns: category | date | day | time | note.
  Muhurat: { sheetId: '14L-MuYdIGTUv0C7vBIY-xHOvANW6hx_-QKwF3nTHjgo' },
}

// Header aliases: the client uses natural column names ("Sun Sign", "Prediction",
// "Horoscope – English") rather than the documented keys (sign, text). Map the
// common variants so their headers work without edits. Keys are lower-cased
// header labels; anything unmapped keeps its own lower-cased label.
const HEADER_ALIAS: Record<string, string> = {
  'sun sign': 'sign',
  'sign name': 'sign',
  zodiac: 'sign',
  rashi: 'sign',
  prediction: 'text',
  reading: 'text',
  horoscope: 'text',
  'horoscope – english': 'text', // en-dash, as the client's daily sheet uses
  'horoscope - english': 'text', // hyphen variant, just in case
  'horoscope english': 'text',
  forecast: 'text',
  outlook: 'text',
  // Hindi reading → its own key so the UI can offer a language toggle.
  'horoscope – hindi': 'text_hi',
  'horoscope - hindi': 'text_hi',
  'horoscope hindi': 'text_hi',
  hindi: 'text_hi',
}

function gvizUrl(tab: string) {
  const src = TAB_SOURCE[tab] ?? {}
  const id = src.sheetId ?? SHEET_ID
  const sheetParam = src.tab ? `&sheet=${encodeURIComponent(src.tab)}` : ''
  return `https://docs.google.com/spreadsheets/d/${id}/gviz/tq?tqx=out:json&headers=1${sheetParam}`
}

/* gviz wraps the JSON in a JS callback; slice to the object and parse. */
function parseGviz(text: string): SheetRow[] {
  const start = text.indexOf('{')
  const end = text.lastIndexOf('}')
  if (start < 0 || end < 0) return []
  const json = JSON.parse(text.slice(start, end + 1))
  const cols: string[] = (json.table?.cols ?? []).map((c: { label?: string }, i: number) => {
    const label = String(c.label || `col${i}`).trim().toLowerCase()
    return HEADER_ALIAS[label] ?? label
  })
  return (json.table?.rows ?? [])
    .map((r: { c: ({ v: unknown; f?: string } | null)[] }) => {
      const obj: SheetRow = {}
      r.c.forEach((cell, i) => {
        const key = cols[i] || `col${i}`
        obj[key] = cell && cell.v != null ? String(cell.f ?? cell.v) : ''
      })
      return obj
    })
    // drop fully-empty rows
    .filter((row: SheetRow) => Object.values(row).some((v) => v.trim() !== ''))
}

const cacheKey = (tab: string) => `lc-sheet:${tab}`

function readCache(tab: string): SheetRow[] | null {
  try {
    const raw = localStorage.getItem(cacheKey(tab))
    return raw ? (JSON.parse(raw) as SheetRow[]) : null
  } catch {
    return null
  }
}

function writeCache(tab: string, rows: SheetRow[]) {
  try {
    localStorage.setItem(cacheKey(tab), JSON.stringify(rows))
  } catch {
    /* private mode / quota — ignore, we still have in-memory rows */
  }
}

export type SheetStatus = 'demo' | 'loading' | 'live' | 'error'

/**
 * Read a sheet tab. Returns the freshest data available: live fetch → last
 * cached → bundled fallback. `status` is 'demo' when no sheet is configured.
 */
export function useSheet(tab: string, fallback: SheetRow[] = []) {
  const [rows, setRows] = useState<SheetRow[]>(() => readCache(tab) ?? fallback)
  const [status, setStatus] = useState<SheetStatus>(SHEET_ID ? 'loading' : 'demo')

  useEffect(() => {
    if (!SHEET_ID) return
    let alive = true
    setStatus('loading')
    fetch(gvizUrl(tab))
      .then((r) => r.text())
      .then((text) => {
        if (!alive) return
        const parsed = parseGviz(text)
        if (parsed.length) {
          setRows(parsed)
          writeCache(tab, parsed)
        }
        setStatus('live')
      })
      .catch(() => {
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [tab])

  return { rows, status }
}

/** True once a real sheet ID is configured (vs. bundled demo content). */
export const sheetConfigured = !!SHEET_ID
